import React from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Helmet } from 'react-helmet-async';
import { useHistory, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import BasicPageLayout from '@/components/common/BasicPageLayout';
import Loader from '@/components/common/Loader';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useMobileView from '@/hooks/ui/useMobileView';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import { BulkActionStatus, BulkEditActions } from '@/modules/BulkEdit';
import {
  ConstituentSearchQueryProvider,
  FilterList,
  LifecycleSearchControls,
} from '@/modules/ConstituentSearch';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import getUpdatedBoardPosition from '../lib/getUpdatedBoardPosition';
import {
  LifecycleBoardProvider,
  useLifecycleBoardContext,
} from './LifecycleBoardContext';
import LifecycleBoardSwitcher from './LifecycleBoardSwitcher';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import LifecycleCardMenu from './LifecycleCardMenu';
import LifecycleColumn from './LifecycleColumn';

export function BaseLifecycleBoard() {
  const history = useHistory();
  const { closeDialog, openDialog } = useDialogManager();
  const {
    addColumn,
    board,
    columns,
    error,
    getConstituent,
    moveConstituent,
    removeColumn,
    updateConstituentPosition,
    updateColumnInfo,
  } = useLifecycleBoardContext();
  const [activeMenu, setActiveMenu] = React.useState(null);
  const isMobile = useMobileView();
  const { isSuperAdmin, isOrgAdmin, isOrgEditor } = useCurrentUser();
  const canEditBoard = isSuperAdmin || isOrgAdmin;
  const isDragDisabled = isMobile || !(isOrgAdmin || isOrgEditor);

  React.useEffect(() => {
    if (!error) return;
    // Probably erroring because they're trying to go to a board that has been
    // deleted. Redirect to /boards instead.
    history.push(AuthRoutes.BOARDS);
  }, [error]);

  const onDragEnd = ({ source, destination, draggableId: constituentId }) => {
    if (
      !(source && destination && constituentId) ||
      source?.droppableId === destination?.droppableId
    ) {
      return;
    }
    const getColumnId = (str) => str.split('-').pop();
    const from = getColumnId(source?.droppableId);
    const to = getColumnId(destination?.droppableId);
    const constituent = getConstituent(constituentId);
    const updatedConstituent = getUpdatedBoardPosition(constituent, {
      boardId: board?.boardId,
      columnId: to,
    });
    moveConstituent(updatedConstituent, from, to);
    updateConstituentPosition({
      constituentId,
      columnId: to,
    });
  };

  const handleAdd = (index) => async (e) => {
    await addColumn(index);
    e.target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  };

  const handleCancel = (column) => {
    // No need to do anything if cancelling an update for an existing column.
    if (!column.isNew) return;
    // If cancelling a new column, remove that column from the board.
    removeColumn(column.columnId);
  };

  const handleRemove = (column) => {
    setActiveMenu(null);
    openDialog(Dialogs.CONFIRMATION, {
      children: translate('BOARD_REMOVE_COLUMN_CONFIRMATION_DESCRIPTION', {
        column: column.columnTitle,
      }),
      confirmLabel: translate('REMOVE'),
      headerText: translate('BOARD_REMOVE_COLUMN_CONFIRMATION_TITLE'),
      onCancel: closeDialog,
      onClose: closeDialog,
      onConfirm: async () => {
        await removeColumn(column.columnId);
        closeDialog();
      },
    });
  };

  const [selectedItems, setSelectedItems] = React.useState([]);
  const [excludedItems, setExcludedItems] = React.useState([]);
  const [query, setQuery] = React.useState([]);

  return (
    <div
      className="flex h-screen w-full flex-col md:pl-2"
      data-testid="lifecycle-board"
    >
      <Helmet>
        {board && (
          <title>
            {translate('BOARD')} | {board.boardTitle}
          </title>
        )}
      </Helmet>
      <LifecycleBoardSwitcher />
      <BulkActionStatus className="mx-4 mt-4" />
      {selectedItems.length ? (
        <div className="mx-3 mt-4">
          <BulkEditActions
            allowMultiSelect
            clearSelectedItems={() => {
              setExcludedItems([]);
              setSelectedItems([]);
              setQuery([]);
            }}
            excludedItems={excludedItems}
            query={query}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </div>
      ) : (
        <>
          <LifecycleSearchControls
            boardId={board?.boardId}
            className="mx-4 mt-4"
          />
          <FilterList className="mx-4 mt-4 shrink-0" />
        </>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="w-full flex-grow overflow-x-scroll">
          <div className="flex h-full px-2 py-4">
            {board ? (
              <>
                {columns.map((column, i) => (
                  <LifecycleColumn
                    className="mx-2 w-full max-w-xs flex-shrink-0 flex-grow basis-75"
                    columnId={column.isNew ? null : column.columnId}
                    isDragDisabled={isDragDisabled}
                    description={column.columnDescription}
                    name={column.columnTitle}
                    key={column.columnId}
                    onCancelUpdate={() => handleCancel(column)}
                    onMenuClick={setActiveMenu}
                    onRemove={() => handleRemove(column)}
                    onUpdate={(e) => updateColumnInfo(column, e)}
                    dataTestid={`lifecycle-column-${i}`}
                    query={query}
                    setQuery={setQuery}
                    selectedItems={selectedItems}
                    setExcludedItems={setExcludedItems}
                    setSelectedItems={setSelectedItems}
                  />
                ))}
                {canEditBoard && (
                  <div className="flex-shrink-0 basis-75 self-start">
                    <button
                      type="button"
                      onClick={handleAdd(columns.length)}
                      className="mx-2 flex w-72 items-center justify-start whitespace-nowrap rounded-lg border border-dashed border-secondary-main bg-secondaryButton-main/20 p-3 text-secondaryButton-contrastText"
                      data-testid="lifecycle-add-btn"
                    >
                      <AddIcon className="mr-3" />
                      {translate('BOARD_ADD_COLUMN')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Loader isCentered className="h-screen w-full items-center" />
            )}
          </div>
        </div>
      </DragDropContext>
      <LifecycleCardMenu
        boardId={board?.boardId}
        columnId={activeMenu?.columnId}
        constituentId={activeMenu?.constituent?.constituentId}
        target={activeMenu?.target}
        onClose={() => setActiveMenu(null)}
        open={!!activeMenu}
      />
    </div>
  );
}

function LifecycleBoard() {
  const { boardId } = useParams();
  return (
    <BasicPageLayout>
      <ConstituentSearchQueryProvider>
        <LifecycleBoardProvider boardId={boardId}>
          <BaseLifecycleBoard />
        </LifecycleBoardProvider>
      </ConstituentSearchQueryProvider>
    </BasicPageLayout>
  );
}

export default LifecycleBoard;
